import { Github, Linkedin, Mail, Send, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { ConfettiButton } from "../confetti-button";
import { PulsingIcon } from "../animated-icons";
import { theme } from "../../theme.js";
import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Contact = () => {
  const form = useRef();
  const [loading, setLoading] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    emailjs
      .sendForm(
        "service_sx3oamb",
        "template_txyzt3s",
        form.current,
        "nHMTj6uq-H9BJY0C6"
      )
      .then(
        (result) => {
          setLoading(false);
          toast.success("Message sent successfully!");
          form.current.reset();
        },
        (error) => {
          setLoading(false);
          toast.error("Message not sent!");
          form.current.reset();
        }
      );
  };

  return (
    <section id="contact" className="relative pt-12 pb-8 md:pt-32 bg-zinc-50 dark:bg-black overflow-hidden body-font">
      {/* Background Decorators */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 blur-[150px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <ToastContainer theme="dark" />
        
        <div className="max-w-6xl mx-auto">
          {/* Huge Typography Heading */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
            className="mb-12 md:mb-20"
          >
            <h2 className="text-4xl md:text-[5rem] lg:text-[7rem] font-bold leading-[0.9] heading-font tracking-tighter text-zinc-900 dark:text-white mb-6">
              LET'S BUILD <br />
              <span className="text-zinc-400 dark:text-zinc-600">SOMETHING</span> <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-indigo-600 dark:from-cyan-400 dark:to-indigo-500">EXTRAORDINARY.</span>
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* The Form - Minimalist */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <form ref={form} onSubmit={sendEmail} className="space-y-12">
                <div className="space-y-2 border-b border-black/20 dark:border-white/20 pb-2 focus-within:border-cyan-600 dark:focus-within:border-cyan-400 transition-colors">
                  <input
                    id="name"
                    name="name"
                    placeholder="What's your name?"
                    className="w-full bg-transparent border-none outline-none text-xl md:text-3xl text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 heading-font py-2"
                    required
                  />
                </div>
                <div className="space-y-2 border-b border-black/20 dark:border-white/20 pb-2 focus-within:border-cyan-600 dark:focus-within:border-cyan-400 transition-colors">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Your email address?"
                    className="w-full bg-transparent border-none outline-none text-xl md:text-3xl text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 heading-font py-2"
                    required
                  />
                </div>
                <div className="space-y-2 border-b border-black/20 dark:border-white/20 pb-2 focus-within:border-cyan-600 dark:focus-within:border-cyan-400 transition-colors">
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell me about your project..."
                    rows={3}
                    className="w-full bg-transparent border-none outline-none text-xl md:text-3xl text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 heading-font py-2 resize-none"
                    required
                  />
                </div>
                
                <div className="pt-4">
                  <ConfettiButton type="Submit" className="group flex items-center gap-4 bg-black dark:bg-white text-white dark:text-black px-8 py-4 rounded-full text-lg font-bold heading-font uppercase tracking-wide hover:bg-cyan-600 dark:hover:bg-cyan-400 transition-colors">
                    {loading ? "Sending..." : "Send Message"} 
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </ConfettiButton>
                </div>
              </form>
            </motion.div>

            {/* Direct Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="flex flex-col justify-between"
            >
              <div className="space-y-12">
                <div>
                  <h4 className="text-zinc-600 dark:text-zinc-500 uppercase tracking-widest text-sm heading-font mb-4">Contact Details</h4>
                  <a href="mailto:md.naimur.rahman.naim@gmail.com" className="text-2xl md:text-3xl text-zinc-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors block heading-font break-all">
                    md.naimur.rahman.naim@gmail.com
                  </a>
                </div>
                
                <div>
                  <h4 className="text-zinc-600 dark:text-zinc-500 uppercase tracking-widest text-sm heading-font mb-4">Socials</h4>
                  <div className="flex gap-6">
                    <a href="https://github.com/Md-Nrahman" target="_blank" rel="noreferrer" className="text-zinc-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-all hover:scale-110">
                      <Github className="w-8 h-8" />
                    </a>
                    <a href="http://linkedin.com/in/mdnrahman/" target="_blank" rel="noreferrer" className="text-zinc-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-all hover:scale-110">
                      <Linkedin className="w-8 h-8" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
      </div>
    </section>
  );
};

export default Contact;
